import { FONT_FAMILY, RACE_COLORS } from "../constants.js";

const sectionStyle = {
  fontFamily: FONT_FAMILY,
  color: "#333",
  lineHeight: 1.7,
  fontSize: 14,
};

const paraStyle = { marginBottom: 14 };
const strongStyle = { fontWeight: 600 };

const formulaBox = {
  marginBottom: 16,
  fontStyle: "italic",
  textAlign: "center",
  background: "#f9f9f9",
  padding: "10px 16px",
  borderRadius: 4,
};

const indentStyle = (color = "#ddd") => ({
  marginBottom: 4,
  fontStyle: "italic",
  paddingLeft: 20,
  borderLeft: `3px solid ${color}`,
  color: color === "#ddd" ? "inherit" : color,
});

const thStyle = (align = "left", color = "#333") => ({
  textAlign: align,
  padding: "6px 14px",
  borderBottom: "1px solid #ddd",
  whiteSpace: "nowrap",
  color,
});

const tdStyle = (align = "left", border = "1px solid #eee", color = "inherit") => ({
  padding: "5px 14px",
  borderBottom: border,
  textAlign: align,
  color,
});

export default function Methodology() {
  return (
    <div style={sectionStyle}>

      {/* Data */}
      <p style={paraStyle}>
        <span style={strongStyle}>Data:</span> All figures are based on stop records for the selected officer and contact type (All, Pedestrian, or Motor Vehicle). Filtering by contact type updates all statistics — including percentages, stop rates, and disparity ratios — to reflect only stops of that type. When "Include stops where data was not recorded" is unchecked, stops with no recorded value for a given category are excluded from all charts and percentages are recalculated among the remaining stops. Only stops made within Cincinnati city limits are included. Contact card records from elsewhere in Hamilton County are excluded. Interstate highway stops are also excluded because highways are not part of any residential neighborhood and therefore motorists stopped there cannot be meaningfully compared against the local population used in this analysis.
      </p>

      {/* Step 1 */}
      <p style={{ marginBottom: 6, fontWeight: 700, fontSize: 15 }}>Step 1: Calculate the weighted population</p>
      <p style={paraStyle}>
        Beat-level population estimates are derived from ACS data at the census tract level. Since census tracts do not align perfectly with CPD beats, we use weighted areal interpolation: for each tract, we calculate what share of its area falls within each beat, then allocate that proportion of the tract's population to the beat. This is done separately for each ACS year (2009–2025), producing a beat × year population estimate that tracks demographic changes over time.
      </p>
      <p style={paraStyle}>
        Census tract boundaries are redrawn every decade following the decennial census. To ensure the population estimates match the geography that was actually in use at the time of each stop, we use the appropriate tract boundary vintage for each year: 2000 boundaries for 2009, 2010 boundaries for 2010–2019, and 2020 boundaries for 2020–2025. Using a single boundary vintage for all years would introduce a mismatch between the population geography and the stop year, which would distort the weighted population estimates — particularly for officers with long careers spanning multiple census decades.
      </p>
      <p style={paraStyle}>
        For each officer, we identify every beat in which they made stops recorded in the contact card dataset and in what years, then calculate what share of their stops occurred in each beat × year combination. Those shares are used to weight the ACS population for each beat and year, producing a single population figure tailored to that officer's actual stop history.
      </p>
      <div style={{ ...formulaBox, lineHeight: 2 }}>
        <div>weight(beat, year) = [stops in (beat, year)] / [total stops]</div>
        <div>Weighted Population = Σ [ weight(beat, year) × ACS population(beat, year) ]</div>
      </div>

      <p style={{ marginBottom: 10, fontWeight: 600 }}>Example: Officer with 30 stops over 3 years (2020–2022) across 2 beats</p>
      <div style={{ overflowX: "auto", marginBottom: 14 }}><table style={{ width: "auto", borderCollapse: "collapse", fontSize: 13 }}>
        <thead>
          <tr style={{ background: "#f5f5f5" }}>
            <th style={thStyle("left")}>Year</th>
            <th style={thStyle("right")}>Beat A stops</th>
            <th style={thStyle("right")}>Beat B stops</th>
            <th style={thStyle("right")}>Total</th>
          </tr>
        </thead>
        <tbody>
          {[["2020",5,2,7],["2021",8,4,12],["2022",5,6,11]].map(([yr,a,b,t]) => (
            <tr key={yr}>
              <td style={tdStyle("left")}>{yr}</td>
              <td style={tdStyle("right")}>{a}</td>
              <td style={tdStyle("right")}>{b}</td>
              <td style={tdStyle("right")}>{t}</td>
            </tr>
          ))}
          <tr style={{ fontWeight: 600, background: "#f5f5f5" }}>
            <td style={{ padding: "5px 14px" }}>Total</td>
            <td style={{ textAlign: "right", padding: "5px 14px" }}>18 (60%)</td>
            <td style={{ textAlign: "right", padding: "5px 14px" }}>12 (40%)</td>
            <td style={{ textAlign: "right", padding: "5px 14px" }}>30</td>
          </tr>
        </tbody>
      </table></div>

      <p style={{ marginBottom: 6 }}>The ACS provides separate <span style={{ color: RACE_COLORS.BLACK, fontWeight: 600 }}>Black</span> and <span style={{ color: RACE_COLORS.WHITE, fontWeight: 600 }}>White</span> population estimates for each beat and year:</p>
      <div style={{ overflowX: "auto", marginBottom: 14 }}><table style={{ width: "auto", borderCollapse: "collapse", fontSize: 13 }}>
        <thead>
          <tr style={{ background: "#f5f5f5" }}>
            <th style={thStyle("left")}>Beat × Year</th>
            <th style={thStyle("right")}>Share of stops</th>
            <th style={thStyle("right", RACE_COLORS.BLACK)}>Black pop (ACS)</th>
            <th style={thStyle("right", RACE_COLORS.WHITE)}>White pop (ACS)</th>
          </tr>
        </thead>
        <tbody>
          {[
            ["Beat A, 2020","5/30 = 16.7%","1,000","500",false],
            ["Beat B, 2020","2/30 = 6.7%","300","2,000",true],
            ["Beat A, 2021","8/30 = 26.7%","1,020","510",false],
            ["Beat B, 2021","4/30 = 13.3%","310","2,020",true],
            ["Beat A, 2022","5/30 = 16.7%","1,040","520",false],
            ["Beat B, 2022","6/30 = 20.0%","320","2,040",true],
          ].map(([by,sh,bp,wp,isGroupEnd]) => (
            <tr key={by}>
              <td style={tdStyle("left", isGroupEnd ? "2px solid #bbb" : "1px solid #eee")}>{by}</td>
              <td style={tdStyle("right", isGroupEnd ? "2px solid #bbb" : "1px solid #eee")}>{sh}</td>
              <td style={tdStyle("right", isGroupEnd ? "2px solid #bbb" : "1px solid #eee", RACE_COLORS.BLACK)}>{bp}</td>
              <td style={tdStyle("right", isGroupEnd ? "2px solid #bbb" : "1px solid #eee", RACE_COLORS.WHITE)}>{wp}</td>
            </tr>
          ))}
        </tbody>
      </table></div>

      <p style={indentStyle(RACE_COLORS.BLACK)}>
        Weighted Black pop = (0.167×1,000) + (0.067×300) + (0.267×1,020) + (0.133×310) + (0.167×1,040) + (0.200×320) = <strong>737</strong>
      </p>
      <p style={{ ...indentStyle(RACE_COLORS.WHITE), marginBottom: 24 }}>
        Weighted White pop = (0.167×500) + (0.067×2,000) + (0.267×510) + (0.133×2,020) + (0.167×520) + (0.200×2,040) = <strong>1,117</strong>
      </p>

      {/* Step 2 */}
      <p style={{ marginBottom: 6, fontWeight: 700, fontSize: 15 }}>Step 2: Calculate stop rates</p>
      <p style={paraStyle}>
        Using the weighted population from Step 1, stop rates express how many people of each race the officer stopped per 1,000 residents per year, based on where and when they actually made stops.
      </p>
      <div style={formulaBox}>
        Stop Rate = (# of stops for race group ÷ years active) ÷ (weighted population for race group ÷ 1,000)
      </div>
      <p style={paraStyle}>
        Stop rates are expressed per 1,000 residents rather than as raw proportions because it makes the numbers easier to interpret. For a city the size of Cincinnati, per 1,000 is the standard scale — it avoids very small decimals while keeping the numbers intuitive. Larger jurisdictions often use per 100,000 or even per million for national-level indicators, but at the city level, per 1,000 strikes the right balance between readability and precision.
      </p>
      <p style={{ marginBottom: 6 }}>If this officer stopped <span style={{ color: RACE_COLORS.BLACK }}><strong>20</strong> Black people</span> and <span style={{ color: RACE_COLORS.WHITE }}><strong>4</strong> White people</span> over 3 years:</p>
      <p style={indentStyle(RACE_COLORS.BLACK)}>Black stop rate = (<strong>20</strong> ÷ 3) ÷ (<strong>737</strong> ÷ 1,000) = 6.67 ÷ 0.737 = <strong>9.05 per 1,000 Black residents per year</strong></p>
      <p style={{ ...indentStyle(RACE_COLORS.WHITE), marginBottom: 24 }}>White stop rate = (<strong>4</strong> ÷ 3) ÷ (<strong>1,117</strong> ÷ 1,000) = 1.33 ÷ 1.117 = <strong>1.19 per 1,000 White residents per year</strong></p>

      {/* Step 3 */}
      <p style={{ marginBottom: 6, fontWeight: 700, fontSize: 15 }}>Step 3: Calculate the disparity ratio</p>
      <p style={paraStyle}>
        Compares stop rates between Black and White residents. A value of 1.00x means both groups were stopped at the same rate relative to their population. Disparities shown in <span style={{ color: RACE_COLORS.BLACK, fontWeight: 600 }}>purple</span> mean Black residents were stopped at a higher rate. Disparities shown in <span style={{ color: RACE_COLORS.WHITE, fontWeight: 600 }}>pink</span> mean White residents were stopped at a higher rate.
      </p>
      <div style={formulaBox}>
        Disparity Ratio = <span style={{ color: RACE_COLORS.BLACK, fontWeight: 600 }}>Black Stop Rate</span> ÷ <span style={{ color: RACE_COLORS.WHITE, fontWeight: 600 }}>White Stop Rate</span>
      </div>
      <p style={{ ...indentStyle("#ddd"), marginBottom: 8 }}>Disparity ratio = <span style={{ color: RACE_COLORS.BLACK, fontWeight: 600 }}>9.05</span> ÷ <span style={{ color: RACE_COLORS.WHITE, fontWeight: 600 }}>1.19</span> = <span style={{ color: RACE_COLORS.BLACK, fontWeight: 700 }}>7.60x</span></p>
      <p style={{ marginBottom: 24, paddingLeft: 20, color: "#444" }}><strong>Interpretation:</strong> From 2020 to 2022, this officer stopped <span style={{ color: RACE_COLORS.BLACK, fontWeight: 600 }}>Black</span> residents at <span style={{ color: RACE_COLORS.BLACK, fontWeight: 700 }}>7.60x</span> the rate of <span style={{ color: RACE_COLORS.WHITE, fontWeight: 600 }}>White</span> residents.</p>

      {/* Charts */}
      <p style={{ marginBottom: 6, fontWeight: 700, fontSize: 15 }}>Charts</p>
      <p style={paraStyle}>
        <span style={strongStyle}>Bar charts:</span> Bars show how this officer's stops for each racial group break down by category. For example, of all stops involving a Black resident, what share ended in a citation? Each race adds up to 100%.
      </p>
      <p style={paraStyle}>
        <span style={strongStyle}>Disparity ratio charts:</span> For each category shown in the bar charts, the disparity ratio compares how common that outcome was for Black residents versus White residents once stopped:
      </p>
      <p style={{ ...paraStyle, fontStyle: "italic", paddingLeft: 20, borderLeft: "3px solid #ddd" }}>
        Disparity Ratio = (% of Black stops with outcome) ÷ (% of White stops with outcome)
      </p>
      <p style={paraStyle}>
        A value of 1x means both groups had the same rate. Values to the right of 1x (<span style={{ color: RACE_COLORS.BLACK, fontWeight: 600 }}>purple</span>) mean Black residents were more likely to experience that outcome; values to the left (<span style={{ color: RACE_COLORS.WHITE, fontWeight: 600 }}>pink</span>) mean White residents were more likely. If a category has no dot, there were no stops of one or both races in that category and a ratio could not be computed. These ratios only capture what happens after a stop has already been made — the overview statistics separately show whether Black and White residents were stopped at different rates to begin with.
      </p>
      <p style={paraStyle}>
        <span style={strongStyle}>Stops over time:</span> Raw monthly counts of stops, not adjusted for population.
      </p>
      <p style={paraStyle}>
        Only charts with sufficient data for the selected officer and contact type are displayed. If data is unavailable, no chart is generated for that section.
      </p>
      <p style={{ marginBottom: 0 }}>
        Figures based on fewer than 30 stops should be interpreted with caution. Small counts may not provide enough data to be reliable.
      </p>
    </div>
  );
}
